import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import MembershipForm, { IMembershipFormData } from '../../components/forms/MembershipForm';
import MembershipMembersOverview from '../../components/MembershipMembersOverview';
import { ConfirmModal } from '../../components/ui';
import { loadSeasonOptions, type SeasonOption } from '../../utils/seasonOptions';
import type { DivisionScheme } from '../../schemaForm/fields/divisionScheme';
import { translateError } from '../../utils/errorMessages';

interface IMembershipSeason {
  _id: number;
  name: string;
  termStart: string | null;
  termEnd: string | null;
}

interface IMembership {
  _id: string;
  name: string;
  description: string;
  type?: 'season' | 'calendar' | 'time' | 'entry';
  yearlyPrice: number;
  entryCount?: number | null;
  calendarConfig?: { startMonth: number; intervals: Partial<Record<'yearly' | 'half_year' | 'quarter' | 'monthly', number>> };
  prices?: Array<{ durationDays: number; price: number; label: string; sortOrder: number; isDefault: boolean }>;
  // Season-division: the split definition (fractions) + the seasons this reusable
  // membership is attached to (M:N truth) + how many reuse it (shared-use warning).
  divisionScheme?: DivisionScheme | null;
  seasons?: IMembershipSeason[];
  usedBySeasonCount?: number;
  // The loose group / member / activity links were retired; the standalone
  // edit page no longer reads or writes them.
  courseId?: number | null;
}

const MembershipEditPage: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { id } = useParams<{ id: string }>();
  const [membership, setMembership] = useState<IMembership | null>(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'form' | 'members'>('form');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [seasonOptions, setSeasonOptions] = useState<SeasonOption[]>([]);

  const navigate = useNavigate();

  useEffect(() => {
    // Season attachment picker options (best-effort; the form still works if the
    // fetch fails — attachments are simply left untouched on save).
    loadSeasonOptions().then(setSeasonOptions).catch(() => setSeasonOptions([]));
  }, []);

  useEffect(() => {
    const fetchMembership = async () => {
      try {
        const { data } = await api.memberships.get(id!);
        setMembership(data as IMembership);
        setLoading(false);
      } catch (err: any) {
        setError(translateError(err, t));
        setLoading(false);
        if (err.response && err.response.status === 404) {
          navigate('/admin/memberships');
        }
      }
    };

    if (id) {
      fetchMembership();
    }
  }, [id, navigate]);

  const handleSubmit = async (data: IMembershipFormData) => {
    setSubmitting(true);
    setError(null);

    try {
      // The loose group / member / activity links were retired in the
      // membership→course refactor; the form no longer carries them.
      await api.memberships.update(id!, data);
      addToast(t('memberships.editTitle') + ' - ' + t('common.save') + '!', 'success');
      navigate('/admin/memberships');
    } catch (err: any) {
      const errorMessage = translateError(err, t);
      setError(errorMessage);
      addToast(t('common.error') + ': ' + errorMessage, 'error');
      setSubmitting(false);
    }
  };

  const handleDelete = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      await api.memberships.delete(id!);
      addToast(t('memberships.title') + ' - ' + t('common.delete') + '!', 'success');
      navigate('/admin/memberships');
    } catch (err: any) {
      const errorMessage = translateError(err, t);
      setError(errorMessage);
      addToast(t('common.error') + ': ' + errorMessage, 'error');
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  if (loading && !membership) return <p>{t('memberships.loadingMembership')}</p>;
  if (error && !membership) return <p className="error-message">{t('common.error')}: {error}</p>;
  if (!membership) return <p>{t('memberships.membershipNotFound')}</p>;

  const initialData: Partial<IMembershipFormData> = {
    name: membership.name,
    description: membership.description || '',
    type: membership.type || 'time',
    yearlyPrice: membership.yearlyPrice || 0,
    entryCount: membership.entryCount,
    prices: membership.prices || [],
    calendarConfig: membership.calendarConfig,
    divisionScheme: membership.divisionScheme ?? undefined,
    seasonIds: (membership.seasons ?? []).map((s) => s._id),
    courseId: membership.courseId ?? null,
  };

  // Live preview against the first attached season's term (a reusable membership
  // may span several — the preview picks one representative term).
  const previewSeason = membership.seasons?.find((s) => s.termStart && s.termEnd) ?? null;

  return (
    <div className="admin-page-content">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          flexWrap: 'wrap',
          gap: '0.5rem',
        }}
      >
        <h2 style={{ margin: 0 }}>{t('memberships.editTitle')}</h2>
      </div>

      {/* Mobile Tab Switcher */}
      <div className="tab-switcher mobile-only">
        <button
          className={activeTab === 'form' ? 'active' : ''}
          onClick={() => setActiveTab('form')}
        >
          {t('memberships.tabEdit')}
        </button>
        <button
          className={activeTab === 'members' ? 'active' : ''}
          onClick={() => setActiveTab('members')}
        >
          {t('memberships.tabMembers')}
        </button>
      </div>

      <div className="event-edit-layout">
        <div className={`tab-content ${activeTab === 'form' ? 'active' : ''} form-section`}>
          {error && <p className="error-message">{t('common.error')}: {error}</p>}
          <MembershipForm
            initialData={initialData}
            seedKey={membership._id}
            onSubmit={handleSubmit}
            loading={submitting}
            submitButtonText={t('memberships.updateButton')}
            onDelete={handleDelete}
            deleteButtonText={t('common.delete')}
            courseId={membership.courseId ?? null}
            previewTermStart={previewSeason?.termStart ?? null}
            previewTermEnd={previewSeason?.termEnd ?? null}
            previewSeasons={seasonOptions}
            defaultPreviewSeasonId={membership.seasons?.[0]?._id}
            usedBySeasonCount={membership.usedBySeasonCount ?? membership.seasons?.length ?? 0}
            seasonOptions={seasonOptions}
          />
        </div>

        <div className={`tab-content ${activeTab === 'members' ? 'active' : ''} attendance-section`}>
          {id && (
            <MembershipMembersOverview
              membershipId={id}
              yearlyPrice={membership.yearlyPrice}
              membershipType={membership.type || 'time'}
              calendarConfig={membership.calendarConfig}
              authToken={JSON.parse(localStorage.getItem('userInfo') || '{}').token}
            />
          )}
        </div>
      </div>

      <Link to="/admin/memberships" className="form-link">
        {t('memberships.goBack')}
      </Link>

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('memberships.deleteTitle')}
        message={t('memberships.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default MembershipEditPage;
